import { Box, Button, Checkbox, Flex } from '@mantine/core';
/* eslint-disable max-statements */
import { cloneDeep } from 'lodash';
import { useEffect, useState } from 'react';

import { Document } from '@e-origin/shared';

import { IconFactory } from '@assets';
import { Table, TableColumnProps } from '../Table';
import { TableActionEnum, TableActions } from '../TableActions';
import { ManageSupportingDocumentView } from './ManageSupportingDocumentView';

export interface ManageSupportingDocumentsRow {
  selected?: boolean;
  document?: Document;
}

interface ManageSupportingDocumentsTableProps {
  createNewDocuments?: boolean;
  documents: ManageSupportingDocumentsRow[];
  onChange(rows: ManageSupportingDocumentsRow[]): void;
  disabled?: boolean;
  declarationGroup?: string;
}

export const ManageSupportingDocumentsTable: React.FC<ManageSupportingDocumentsTableProps> = (props) => {
  const { createNewDocuments, documents, onChange, disabled, declarationGroup } = props;

  const [data, setData] = useState<ManageSupportingDocumentsRow[]>([]);

  const [showModal, setShowModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | undefined>(undefined);

  const toggleShowDocumentModal = () => {
    setShowModal(!showModal);
  };

  const handleCheckRow = (row: ManageSupportingDocumentsRow) => {
    setData(
      data.map((d) => {
        return d === row
          ? {
              selected: !row.selected,
              document: row.document,
            }
          : d;
      }),
    );
  };

  const handleTableAction = (actionType: any, row: ManageSupportingDocumentsRow) => {
    switch (actionType as string) {
      case 'view':
        setSelectedDocument(row.document);
        toggleShowDocumentModal();
        break;
      default:
        break;
    }
  };

  const columns: TableColumnProps<ManageSupportingDocumentsRow>[] = [
    {
      name: 'Selection',
      cell: (row: ManageSupportingDocumentsRow) => <Checkbox checked={row.selected} onChange={() => handleCheckRow(row)} type={'checkbox'} />,
    },
    {
      name: 'Code',
      cell: (row) => row.document.type,
    },
    {
      name: 'Reference',
      cell: (row) => row.document.referenceNumber,
    },
    {
      name: 'Date Of Validity',
      cell: (row) => row.document.dateOfValidity,
    },
    {
      name: 'Actions',
      sticky: 'right',
      cell: (row: ManageSupportingDocumentsRow) => (
        <TableActions
          actions={[
            {
              icon: IconFactory.view,
              text: 'View details',
              onClick: () => handleTableAction('view', row),
            },
          ]}
          onClick={(actionType) => {
            handleTableAction(actionType as TableActionEnum, row);
          }}
        />
      ),
    },
  ];

  useEffect(() => {
    if (documents && !data.length) {
      setData(cloneDeep(documents));
    }
  }, [documents]);

  useEffect(() => {
    if (!data?.length) {
      return;
    }
    onChange(data);
  }, [data]);

  const handleSavingDocument = (documentData: Document, originalDocument: Document) => {
    if (!originalDocument) {
      setData([{ selected: true, document: documentData }, ...data]);
      return;
    }

    setData(
      data.map((d) => {
        if (d.document === originalDocument) {
          return { selected: true, document: documentData };
        }
        return d;
      }),
    );
  };

  return (
    <>
      {showModal && (
        <ManageSupportingDocumentView
          opened={showModal}
          onHide={() => {
            toggleShowDocumentModal();
            setSelectedDocument(undefined);
          }}
          docDetails={selectedDocument}
          onSave={handleSavingDocument}
          editDisabled={disabled}
          declarationGroup={declarationGroup}
        />
      )}
      {!!createNewDocuments && (
        <Flex justify={'flex-end'}>
          {!disabled && (
            <Button
              onClick={(event) => {
                event.preventDefault();
                toggleShowDocumentModal();
              }}
              variant="outline"
              size="lg"
            >
              Add new
            </Button>
          )}
        </Flex>
      )}
      <Box mt="lg">
        <Table columns={columns} dataSource={data || []} defaultPerPage={5} pagination verticalSpacing="xs" />
      </Box>
    </>
  );
};
